import {useEffect, useRef, useState} from 'react';
import {debounce, isEmpty} from 'lodash';

import {PRE_JOURNEY} from '/enum/clientJourneyTasks.enum';

import {
  getNewTasks,
  getTaskDataToSave,
  getTaskDataWithMeta,
  getTasksOnChange,
  getTasksWithLabel,
  getUpdatedTasks,
} from '../ClientJourneyTask.util';

/**
 * @param {object} params - Parameters
 * @param {Object[]} params.filteredTasks - an object that contains tile data (description)
 * @param {string}   params.filteredTasks.tabDescription
 * @param {string}   params.filteredTasks.screenTabCode
 * @param {Object[]} params.filteredTasks.checkBox
 * @param {string}   params.filteredTasks.checkBox.screenTabCode
 * @param {string}   params.filteredTasks.checkBox.chkBoxCode
 * @param {string}   params.filteredTasks.checkBox.description
 * @param {Object[]} params.preJourneyTasks - an object that contains data about the status of the checkbox
 * @param {number}   params.preJourneyTasks.clientId
 * @param {string}   params.preJourneyTasks.screenTabCode
 * @param {Object[]} params.preJourneyTasks.checkIns
 * @param {string}   params.preJourneyTasks.checkIns.chkBoxCode
 * @param {boolean}  params.preJourneyTasks.checkIns.checkStatus
 * @param {function} params.fetchClientJourneyTask - function for sending a post request to change the status of the checkbox
 * @param {function} params.setClientJourneyTasks - function for changing the status in the redux
 * @param {boolean}  params.hasPreJourneyTasks - the value for checking the presence of uploaded data. If the data has not been loaded returns false
 *
 * @returns {object} tasks - object for displaying data in a FlatList
 * @returns {function} getTaskItemIcon - return name of icon for tile: 'checkbox-selected' or 'checkbox-unselected'
 * @returns {function} onTaskStatusChange - function fur changing task status
 */
export const usePreJourney = ({
  preJourneyInitialTasks,
  preJourneyTasks,
  fetchClientJourneyTask,
  setClientJourneyTasks,
  clientId,
  clientJourneyTimer,

  // calling the client journey loading state to reload ClientsToContact nextTask filter
  setClientJourneyState,
}: any) => {
  const tasksRef = useRef([]);
  const timer = useRef(null);
  const [newPreJourneyTasks, setNewPreJourneyTasks] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [isCheckboxStatusChanged, setIsCheckboxStatusChanged] = useState(false);

  /**
   * @param {boolean} taskStatus
   */
  const getTaskItemIcon = (taskStatus: any) =>
    taskStatus ? 'checkbox-selected' : 'checkbox-unselected';

  const savePreJourneyTaskData = (data: any) => {
    // Send the request with new data
    fetchClientJourneyTask({
      task: getTaskDataWithMeta(newPreJourneyTasks, data, PRE_JOURNEY),
    });
  };

  const onTaskStatusChange = (status: any, id: any) => {
    setIsCheckboxStatusChanged(true);
    const tasksAfterStatusChange = getTasksOnChange(tasks, id, status);
    setTasks(tasksAfterStatusChange);
    // Save it in redux to use the updated data and avoid delays due to the request
    setClientJourneyTasks({
      // @ts-expect-error TS(2554): Expected 4 arguments, but got 3.
      tasks: getUpdatedTasks(
        tasksAfterStatusChange,
        newPreJourneyTasks,
        PRE_JOURNEY,
      ),
    });
    // @ts-expect-error TS(2769): No overload matches this call.
    clearTimeout(timer.current);
  };

  useEffect(() => {
    setIsCheckboxStatusChanged(false);
    if (isCheckboxStatusChanged) {
      // @ts-expect-error TS(2322): Type 'Timeout' is not assignable to type 'null'.
      timer.current = setTimeout(() => {
        debounce(
          savePreJourneyTaskData,
          clientJourneyTimer,
        )(getTaskDataToSave(tasksRef?.current));
      }, clientJourneyTimer);
      // calling the client journey loading state to reload ClientsToContact nextTask filter
      setClientJourneyState();
    }
  }, [isCheckboxStatusChanged]);

  useEffect(() => {
    if (!isEmpty(newPreJourneyTasks) && !isEmpty(preJourneyInitialTasks)) {
      const updatedTasks = getTasksWithLabel(
        newPreJourneyTasks,
        preJourneyInitialTasks,
      );
      // @ts-expect-error TS(2345): Argument of type 'any[]' is not assignable to para... Remove this comment to see the full error message
      setTasks([...updatedTasks]);
    }
  }, [newPreJourneyTasks, preJourneyInitialTasks]);

  useEffect(() => {
    if (!isEmpty(preJourneyTasks)) {
      setNewPreJourneyTasks(preJourneyTasks);
    }
    if (!isEmpty(preJourneyInitialTasks) && isEmpty(preJourneyTasks)) {
      // @ts-expect-error TS(2345): Argument of type 'any[]' is not assignable to para... Remove this comment to see the full error message
      setNewPreJourneyTasks(getNewTasks(clientId, preJourneyInitialTasks));
    }
  }, [preJourneyTasks, preJourneyInitialTasks]);

  useEffect(() => {
    tasksRef.current = tasks;
  }, [tasks]);

  return {tasks, getTaskItemIcon, onTaskStatusChange};
};
